import React, { useState, useEffect, useMemo } from 'react';
import './DividendTracker.css';

const CRYPTO = new Set(['BTC','ETH','SOL','AVAX','NEAR','SUI','LINK','AAVE','ONDO','HYPE','WLD','TON','TIA','AKT','OP','RNDR','DOGE','XRP','ADA','DOT','MATIC','ATOM','UNI','MKR']);

export default function DividendTracker({ portfolioItems }) {
  const [divData, setDivData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  // Build a map of symbol -> shares for income calculation
  const sharesMap = useMemo(() => {
    const map = {};
    (portfolioItems || []).forEach(p => {
      map[p.symbol.toUpperCase()] = p.shares || 0;
    });
    return map;
  }, [portfolioItems]);

  useEffect(() => {
    let cancelled = false;
    const fetchDividends = async () => {
      if (!portfolioItems || portfolioItems.length === 0) { setLoading(false); return; }
      setLoading(true);
      setError(null);
      try {
        const symbols = portfolioItems
          .map(p => p.symbol)
          .filter(s => !CRYPTO.has(s.toUpperCase()))
          .join(',');
        if (!symbols) { setLoading(false); return; }
        const resp = await fetch(`/api/research/dividend-calendar?symbols=${symbols}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setDivData(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDividends();
    return () => { cancelled = true; };
  }, [portfolioItems]);

  // Condense: one row per ticker, enrich with shares-based calculations
  const condensed = useMemo(() => {
    if (!divData?.dividends) return [];
    const bySymbol = {};
    divData.dividends.forEach(d => {
      const sym = d.symbol;
      if (!bySymbol[sym] || d.ex_date > bySymbol[sym].ex_date) {
        bySymbol[sym] = { ...d };
      }
    });

    return Object.values(bySymbol).map(d => {
      const shares = sharesMap[d.symbol] || 0;
      const annualDiv = d.annual_dividend || 0;
      const annualIncome = shares * annualDiv;
      return { ...d, shares, annual_income: annualIncome };
    }).sort((a, b) => (b.yield || 0) - (a.yield || 0));
  }, [divData, sharesMap]);

  // Calculate portfolio-level summary from actual shares
  const summary = useMemo(() => {
    if (condensed.length === 0) return null;
    const totalIncome = condensed.reduce((s, d) => s + d.annual_income, 0);
    const totalValue = condensed.reduce((s, d) => {
      const item = (portfolioItems || []).find(p => p.symbol.toUpperCase() === d.symbol);
      return s + (item ? (item.shares || 0) * (item.current_price || item.entry_price || 0) : 0);
    }, 0);
    const portfolioYield = totalValue > 0 ? (totalIncome / totalValue) * 100 : 0;

    // Find next ex-date
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = condensed
      .filter(d => d.ex_date && d.ex_date >= today)
      .sort((a, b) => a.ex_date.localeCompare(b.ex_date));
    const nextExDate = upcoming.length > 0 ? { symbol: upcoming[0].symbol, date: upcoming[0].ex_date } : null;

    return { totalIncome, portfolioYield, nextExDate };
  }, [condensed, portfolioItems]);

  if (loading) {
    return (
      <div className="dividend-section">
        <div className="dividend-header"><h3 className="dividend-title">Dividend Tracker</h3></div>
        <div className="dividend-loading">Loading dividend data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dividend-section">
        <div className="dividend-header"><h3 className="dividend-title">Dividend Tracker</h3></div>
        <div className="dividend-error">{error}</div>
      </div>
    );
  }

  if (!divData || condensed.length === 0) return null;

  const fmtCurrency = (v) => v == null ? '—' : `$${v.toFixed(2)}`;
  const fmtPct = (v) => v == null || v === 0 ? '—' : `${v.toFixed(2)}%`;

  return (
    <div className="dividend-section">
      <div className="dividend-header">
        <h3 className="dividend-title">Dividend Tracker</h3>
        <button className="dividend-toggle" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>

      {!collapsed && (
        <>
          {summary && (
            <div className="dividend-summary">
              <div className="dividend-card">
                <span className="dividend-card-label">Est. Annual Income</span>
                <span className="dividend-card-value">{fmtCurrency(summary.totalIncome)}</span>
              </div>
              <div className="dividend-card">
                <span className="dividend-card-label">Portfolio Yield</span>
                <span className="dividend-card-value">{fmtPct(summary.portfolioYield)}</span>
              </div>
              <div className="dividend-card">
                <span className="dividend-card-label">Next Ex-Date</span>
                <span className="dividend-card-value">
                  {summary.nextExDate ? `${summary.nextExDate.symbol} ${summary.nextExDate.date}` : '—'}
                </span>
              </div>
            </div>
          )}

          <div className="dividend-table-wrapper">
            <table className="dividend-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Shares</th>
                  <th>Last Ex-Date</th>
                  <th>Per Share</th>
                  <th>Annual/Share</th>
                  <th>Your Income</th>
                  <th>Yield</th>
                </tr>
              </thead>
              <tbody>
                {condensed.map((d) => (
                  <tr key={d.symbol}>
                    <td className="div-symbol">{d.symbol}</td>
                    <td className="div-mono">{d.shares}</td>
                    <td className="div-mono">{d.ex_date || '—'}</td>
                    <td className="div-mono">{fmtCurrency(d.amount)}</td>
                    <td className="div-mono">{fmtCurrency(d.annual_dividend)}</td>
                    <td className="div-mono div-income">{fmtCurrency(d.annual_income)}</td>
                    <td className="div-mono">{fmtPct(d.yield)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
